<template>
  <div class="flex justify-center w-full py-6">
    <div v-if="pageLoading" class="grid w-full h-128 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else-if="center" class="flex flex-col w-full gap-5 max-w-180">
      <div class="flex items-center justify-between w-full gap-3 px-6">
        <BlTextTitle3 text="Center information" />
        <div class="flex items-center gap-3">
          <BlInputButton
            v-if="center.status === 'active'"
            type="outlined-error"
            label="Deactivate center"
            @clicked="deactivateOpened = true"
          />
          <BlInputButton
            v-if="center.status === 'deactivated'"
            type="outlined"
            label="Reactivate center"
            @clicked="reactivateOpened = true"
          />
          <div class="relative">
            <BlInputButton
              type="plain"
              icon="dots-vertical"
              class="flex items-center justify-center w-8 h-8 hover:bg-hover-state"
              @clicked="menuOpened = !menuOpened"
            />
            <div
              v-if="menuOpened"
              class="absolute right-0 z-50 flex flex-col gap-1 p-2 mt-2 border rounded bg-pure-white border-slate-400"
            >
              <div
                class="px-4 py-2 text-left transition-all duration-300 rounded cursor-pointer hover:bg-hover-state min-w-40"
                :class="{
                  'pointer-events-none bg-slate-300 opacity-25': isCurrentCenter
                }"
                @click="deleteOpened = true"
              >
                <BlTextBody2 class="text-error-400" text="Delete center" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full px-6 py-6 bg-pure-white rounded-2xl">
        <div class="flex items-center justify-between gap-3 mb-4">
          <BlTextTitle4 text="Center details" />
          <BlInputButton
            type="outlined"
            label="Edit"
            @clicked="centerDetailsEditOpened = true"
          />
          <EditCenterDetailsModal
            v-if="centerDetailsEditOpened"
            @close-modal="centerDetailsEditOpened = false"
          />
        </div>
        <div class="grid grid-cols-6 gap-3">
          <BlTextBody2 text="Center name" class="col-span-2 text-slate-700" />
          <BlTextBody2 :text="center.name" class="col-span-4 text-slate-1000" />
          <BlTextBody2 text="Status" class="col-span-2 text-slate-700" />
          <div class="flex col-span-4">
            <BlChip :chip="center.status" dense />
          </div>
          <BlTextBody2 text="Date format" class="col-span-2 text-slate-700" />
          <BlTextBody2
            :text="center.date_format"
            class="col-span-4 text-slate-1000"
          />
          <BlTextBody2 text="Time zone" class="col-span-2 text-slate-700" />
          <BlTextBody2
            :text="center.timezone"
            class="col-span-4 text-slate-1000"
          />
          <BlTextBody2
            text="Currency for Credits in Appointments"
            class="col-span-2 text-slate-700"
          />
          <BlTextBody2
            :text="`${center.currency} (${center.currency_sym})`"
            class="col-span-4 text-slate-1000"
          />
        </div>
      </div>

      <div class="w-full px-6 py-6 bg-pure-white rounded-2xl">
        <div class="flex items-center justify-between gap-3 mb-4">
          <BlTextTitle4 text="Team size limit" />
          <BlInputButton
            type="outlined"
            label="Adjust team size"
            @clicked="teamSizeLimitEditOpened = true"
          />
          <EditTeamSizeLimitModal
            v-if="teamSizeLimitEditOpened"
            @close-modal="teamSizeLimitEditOpened = false"
          />
        </div>
        <div class="grid grid-cols-4 gap-3">
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Total seat limit"
          >
            <BlTextTitle3
              :text="
                center.team_size_limit === null ? '-' : center.team_size_limit
              "
            />
            <div class="w-full text-center truncate">
              <BlTextBody2 text="Total seat limit" class="text-slate-700" />
            </div>
          </div>
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Total active member(s)"
          >
            <BlTextTitle3 :text="center.total_active_members || 0" />
            <div class="w-full text-center truncate">
              <BlTextBody2
                text="Total active member(s)"
                class="text-slate-700"
              />
            </div>
          </div>
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Active therapist(s)"
          >
            <BlTextTitle3 :text="center.total_active_therapists || 0" />
            <div class="w-full text-center truncate">
              <BlTextBody2 text="Active therapist(s)" class="text-slate-700" />
            </div>
          </div>
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Active admin(s)"
          >
            <BlTextTitle3 :text="center.total_active_admins || 0" />
            <div class="w-full text-center truncate">
              <BlTextBody2 text="Active admin(s)" class="text-slate-700" />
            </div>
          </div>
        </div>
        <div
          v-if="exceedTeamSizeLimits > 0"
          class="flex gap-2 px-4 py-2 mt-4 rounded bg-tulip-200"
        >
          <BlIcon icon="warning" color="tulip-7" class="w-4 h-4 mt-1" />
          <div>
            <BlTextBody2
              text="The current subscription is set to"
              class="text-tulip-800"
            />
            <BlTextBody2
              :text="center.team_size_limit || 0"
              class="text-tulip-800"
            />
            <BlTextBody2 text="seats, but there are" class="text-tulip-800" />
            <BlTextBody2
              :text="center.total_active_members"
              class="text-tulip-800"
            />
            <BlTextBody2
              text="active members, exceeding the limit by"
              class="text-tulip-800"
            />
            <BlTextBody2
              :text="
                `${center.total_active_members -
                  (center.team_size_limit || 0)}.`
              "
              class="text-tulip-800"
            />
            <BlTextBody2
              text="Please review the seat allocation."
              class="text-tulip-800"
            />
          </div>
        </div>
      </div>

      <div class="w-full px-6 py-6 bg-pure-white rounded-2xl">
        <div class="flex items-center justify-between gap-3 mb-4">
          <BlTextTitle4 text="Manage features" />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onChangeAnalytics"
            >
              <BlInputToggle
                :checked="center.enable_analytics"
                @changed="onChangeAnalytics"
              />
              <BlTextBody2 text="Analytics" />
            </div>
          </div>
          <div class="flex">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onChangeAppointments"
            >
              <BlInputToggle
                :checked="center.enable_appointment"
                @changed="onChangeAppointments"
              />
              <BlTextBody2 text="Availabilities & appointments" />
            </div>
          </div>
          <div class="flex">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onChangeBranches"
            >
              <BlInputToggle
                :checked="center.enable_branch"
                @changed="onChangeBranches"
              />
              <BlTextBody2 text="Branches" />
            </div>
          </div>
          <div class="flex">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onChangeSalesPipeline"
            >
              <BlInputToggle
                :checked="center.enable_sales_pipeline"
                @changed="onChangeSalesPipeline"
              />
              <BlTextBody2 text="Sales pipeline" />
            </div>
          </div>
          <div class="flex">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onChangeSbt"
            >
              <BlInputToggle
                :checked="center.enable_sbt"
                @changed="onChangeSbt"
              />
              <BlTextBody2 text="Skill-based Treatment (SBT)" />
            </div>
          </div>
        </div>
      </div>

      <div class="w-full px-6 py-6 bg-pure-white rounded-2xl">
        <div class="flex items-center justify-between gap-3 mb-4">
          <BlTextTitle4 text="Databank" />
          <!-- <div class="flex items-center gap-3">
            <BlInputButton
              type="outlined"
              label="Reset"
              @clicked="resetDatabank = true"
            />
            <BlInputButton
              type="outlined"
              label="Copy databank from"
              @clicked="copyDatabank = true"
            />
          </div> -->
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Total targets"
          >
            <BlTextTitle3 :text="center.total_targets || 0" />
            <div class="w-full text-center truncate">
              <BlTextBody2 text="Total targets" class="text-slate-700" />
            </div>
          </div>
          <div
            class="flex flex-col items-center gap-2 px-2 py-2 rounded bg-slate-200"
            title="Total curricula"
          >
            <BlTextTitle3 :text="center.total_curricula || 0" />
            <div class="w-full text-center truncate">
              <BlTextBody2 text="Total curricula" class="text-slate-700" />
            </div>
          </div>
        </div>
      </div>

      <div class="w-full px-6 py-6 bg-pure-white rounded-2xl">
        <div class="flex items-center justify-between gap-3 mb-4">
          <BlTextTitle4 text="Contact information" />
          <BlInputButton
            type="outlined"
            label="Edit"
            @clicked="contactInformationEditOpened = true"
          />
          <EditContactInformationModal
            v-if="contactInformationEditOpened"
            @close-modal="contactInformationEditOpened = false"
          />
        </div>
        <div class="grid grid-cols-6 gap-3">
          <BlTextBody2
            text="Contact person name"
            class="col-span-2 text-slate-700"
          />
          <BlTextBody2
            :text="center.contact_name || '-'"
            class="col-span-4 text-slate-1000"
          />
          <BlTextBody2 text="Email" class="col-span-2 text-slate-700" />
          <BlTextBody2
            :text="center.contact_email || '-'"
            class="col-span-4 text-slate-1000"
          />
          <BlTextBody2 text="Phone number" class="col-span-2 text-slate-700" />
          <BlTextBody2
            :text="center.contact_phone_number || '-'"
            class="col-span-4 text-slate-1000"
          />
          <BlTextBody2
            text="Center address"
            class="col-span-2 text-slate-700"
          />
          <BlTextBody2
            :text="center.center_address || '-'"
            class="col-span-4 text-slate-1000"
          />
        </div>
      </div>
    </div>

    <BlBackdrop
      v-if="copyDatabank"
      :title="`${submitLoading ? 'Copying...' : 'Select databank to copy'}`"
      :label="
        `${submitLoading ? 'Copying...' : `Copy ${selectedCount} databank(s)`}`
      "
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="copyDatabank = false"
      @backdrop-action="onCopyDatabank"
    >
      <div class="px-8">
        <div v-if="submitLoading">
          <div class="relative flex items-center justify-center py-2">
            <div>
              <BlTextBody2
                text="The copying process is underway. Please wait patiently while the operation completes."
              />
            </div>
          </div>
        </div>
        <div v-else>
          <div class="relative flex items-center px-2">
            <input
              type="text"
              v-model="query"
              placeholder="Search ..."
              class="flex-shrink-0 w-full px-4 py-2 text-sm border rounded outline-none border-grey-200 focus:border-light-purple"
            />
            <BlIcon icon="search" class="absolute w-6 right-4" />
          </div>

          <div
            v-if="filteredOptions.length === 0"
            class="relative flex items-center justify-center px-4 py-2"
          >
            <BlTextBody2 text="Option not found" />
          </div>

          <div v-else class="px-2 mt-5 bl-input-dropdown-body">
            <div
              v-for="option in filteredOptions"
              :key="option.id"
              class="bl-input-dropdown-option"
              @click="onChangeValue(option)"
            >
              <BlInputCheck
                class="flex-shrink-0 pointer-events-none"
                :checked="isOptionSelected(option)"
              />
              <div class="bl-input-dropdown-option-name line-clamp-2">
                <div class="font-bold" :style="{ color: '#475467' }">
                  {{ option.label || option.name || option }}
                </div>
                <div
                  class="flex items-center gap-2"
                  :style="{ color: '#475467' }"
                >
                  {{ option.total_targets }} target(s)
                  <div
                    class="w-2 h-2 rounded-full "
                    :style="{ backgroundColor: '#98A2B3' }"
                  ></div>
                  {{ option.total_curriculums }} curricula
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="resetDatabank"
      :title="`${submitLoading ? 'Resetting...' : 'Reset databank'}`"
      :label="`${submitLoading ? 'Resetting...' : 'Reset databank'}`"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="resetDatabank = false"
      @backdrop-action="onResetDatabank"
    >
      <div class="px-8">
        <div v-if="submitLoading">
          <BlTextBody2
            text="The databank reset process is underway. This may take some time. Please be patient while the operation completes."
          />
        </div>
        <div v-else>
          <BlTextBody2
            text="You are about to reset the databank for this center. By proceeding, all targets in the databank will be permanently deleted. This action is irreversible and cannot be undone."
          />
        </div>
      </div>
    </BlBackdrop>
    <BlBackdrop
      v-if="deactivateOpened"
      :title="`Deactivate center`"
      :label="`Deactivate center`"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="deactivateOpened = false"
      @backdrop-action="onDeactivate"
    >
      <div class="px-8">
        <div class="mb-2">
          <BlTextBody2 text="You are about to deactivate the center" />
          <BlTextBody2 :text="`${center.name}.`" class="font-bold" />
        </div>
        <div>
          <BlTextBody2 text="By deactivating this center," />
          <BlTextBody2
            text="all users within the center will lose access to Blüm immediately."
          />
          <BlTextBody2 text="Do you wish to proceed?" />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="reactivateOpened"
      :title="`Reactivate center`"
      :label="`Reactivate center`"
      :submitLoading="submitLoading"
      maxWidth="40rem"
      @backdrop-close="reactivateOpened = false"
      @backdrop-action="onReactivate"
    >
      <div class="px-8">
        <div class="mb-2">
          <BlTextBody2 text="You are about to reactivate the center" />
          <BlTextBody2 :text="`${center.name}.`" class="font-bold" />
        </div>
        <div>
          <BlTextBody2 text="By reactivating this center," />
          <BlTextBody2
            text="all users within the center will immediately regain access to Blüm."
          />
          <BlTextBody2 text="Do you wish to proceed?" />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      :title="`Delete center`"
      :label="`Delete center`"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    >
      <div class="px-8">
        <BlTextBody2 text="By deleting" />
        <BlTextBody2 :text="`${center.name}`" class="font-bold" />
        <BlTextBody2 text="all data associated with it will be lost," />
        <BlTextBody2
          text="and users will no longer be able to access the center."
        />
        <BlTextBody2 text="This action cannot be undone." />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import EditCenterDetailsModal from "./centerInformation/EditCenterDetailsModal.vue";
import EditTeamSizeLimitModal from "./centerInformation/EditTeamSizeLimitModal.vue";
import EditContactInformationModal from "./centerInformation/EditContactInformationModal.vue";

import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlChip from "../../BlChip.vue";
import BlIcon from "../../BlIcon.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlSearchBox from "../../BlSearchBox.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputCheck from "../../BlInputCheck.vue";

export default {
  name: "CenterInformation",
  components: {
    EditCenterDetailsModal,
    EditTeamSizeLimitModal,
    BlInputCheck,
    EditContactInformationModal,
    BlTextTitle3,
    BlInputSearch,
    BlInputRadio,
    BlTextTitle4,
    BlTextBody2,
    BlInputButton,
    BlInputToggle,
    BlSearchBox,
    BlInputText,
    BlChip,
    BlIcon,
    BlBackdrop
  },
  data() {
    return {
      pageLoading: true,
      submitLoading: false,
      menuOpened: false,
      deactivateOpened: false,
      reactivateOpened: false,
      deleteOpened: false,
      copyDatabank: false,
      resetDatabank: false,
      //
      centerDetailsEditOpened: false,
      teamSizeLimitEditOpened: false,
      contactInformationEditOpened: false,
      copyDatabankInput: {
        placeholder: "Search by name",
        model: ""
      },
      copyDatabankOptions: [],
      query: "",
      selectedOptions: []
    };
  },
  computed: {
    ...mapGetters(["currentUser", "center", "databanks"]),
    isCurrentCenter() {
      return this.currentUser.center_id === this.center.id;
    },
    filteredOptions() {
      return this.databanks
        .filter(option => {
          return (
            option.name.toLowerCase().indexOf(this.query.toLowerCase()) > -1
          );
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    selectedCount() {
      return this.selectedOptions.length;
    },
    exceedTeamSizeLimits() {
      if (!this.center) return 0;
      return this.center.total_active_members - this.center.team_size_limit;
    },
    copyDatabankSelected() {
      return this.copyDatabankOptions.find(
        option => option.name === this.copyDatabankInput.model
      );
    },
    isDisabledSubmit() {
      if (!this.selectedCount) return true;
      return false;
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  async mounted() {
    this.pageLoading = true;
    const centerId = Number(this.$route.params.id);
    await this.getCenter({ id: centerId });
    // await this.getDatabanks();
    this.pageLoading = false;
  },
  methods: {
    ...mapActions([
      "getCenter",
      "updateCenter",
      "deleteCenter",
      "getDatabanks",
      "centerDatabankCopy",
      "centerResetDatabank"
    ]),
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    },
    onChangeValue(option) {
      const index = this.selectedOptions.findIndex(opt => opt.id === option.id);
      if (index === -1) {
        this.selectedOptions.push(option);
      } else {
        this.selectedOptions.splice(index, 1);
      }
    },

    isOptionSelected(option) {
      return this.selectedOptions.some(opt => opt.id === option.id);
    },
    async onDeactivate() {
      this.submitLoading = true;
      // active to deactivated
      const params = {
        id: this.center.id,
        center: { status: "deactivated" },
        title: `Center deactivated`,
        message: `Success! The center has been deactivated.`
      };
      const { success } = await this.updateCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.deactivateOpened = false;
    },
    async onReactivate() {
      this.submitLoading = true;
      // deactivated to active
      const params = {
        id: this.center.id,
        center: { status: "active" },
        title: `Center activated`,
        message: `Success! The center has been activated.`
      };
      const { success } = await this.updateCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.reactivateOpened = false;
    },
    async onDelete() {
      this.submitLoading = true;
      const params = {
        id: this.center.id,
        center_name: this.center.name
      };
      const { success } = await this.deleteCenter(params);
      this.submitLoading = false;
      if (!success) return;
      this.deleteOpened = false;
    },
    //
    async onChangeAnalytics() {
      if (this.submitLoading) return;
      this.submitLoading = true;
      const access = !this.center.enable_analytics;
      const act = access ? "enabled" : "disabled";
      const params = {
        id: this.center.id,
        center: { enable_analytics: access },
        title: `Center feature updated`,
        message: `Success! The analytics feature has been <b>${act}</b> in this center.`
      };
      await this.updateCenter(params);
      this.submitLoading = false;
    },
    async onChangeAppointments() {
      if (this.submitLoading) return;
      this.submitLoading = true;
      const access = !this.center.enable_appointment;
      const act = access ? "enabled" : "disabled";
      const params = {
        id: this.center.id,
        center: { enable_appointment: access },
        title: `Center feature updated`,
        message: `Success! The availabilities & appointments feature has been <b>${act}</b> in this center.`
      };
      await this.updateCenter(params);
      this.submitLoading = false;
    },
    async onChangeBranches() {
      if (this.submitLoading) return;
      this.submitLoading = true;
      const access = !this.center.enable_branch;
      const act = access ? "enabled" : "disabled";
      const params = {
        id: this.center.id,
        center: { enable_branch: access },
        title: `Center feature updated`,
        message: `Success! The branches feature has been <b>${act}</b> in this center.`
      };
      await this.updateCenter(params);
      this.submitLoading = false;
    },
    async onChangeSalesPipeline() {
      if (this.submitLoading) return;
      this.submitLoading = true;
      const access = !this.center.enable_sales_pipeline;
      const act = access ? "enabled" : "disabled";
      const params = {
        id: this.center.id,
        center: { enable_sales_pipeline: access },
        title: `Center feature updated`,
        message: `Success! The sales pipeline feature has been <b>${act}</b> in this center.`
      };
      await this.updateCenter(params);
      this.submitLoading = false;
    },
    async onChangeSbt() {
      if (this.submitLoading) return;
      this.submitLoading = true;
      const access = !this.center.enable_sbt;
      const act = access ? "enabled" : "disabled";
      const params = {
        id: this.center.id,
        center: { enable_sbt: access },
        title: `Center feature updated`,
        message: `Success! The skill-based treatment (SBT) feature has been <b>${act}</b> in this center.`
      };
      await this.updateCenter(params);
      this.submitLoading = false;
    },

    async onCopyDatabank() {
      this.submitLoading = true;
      const { success } = await this.centerDatabankCopy({
        id: this.$route.params.id,
        databank_ids: this.selectedOptions.map(opt => opt.id)
      });
      this.submitLoading = false;
      this.selectedOptions = [];
      if (!success) return;
      this.copyDatabank = false;
    },

    async onResetDatabank() {
      this.submitLoading = true;
      const { success } = await this.centerResetDatabank({
        id: this.$route.params.id
      });
      this.submitLoading = false;
      if (!success) return;
      this.resetDatabank = false;
    }
  }
};
</script>

<style scoped lang="scss">
.bl-input-dropdown-body {
  width: 100%;
  max-height: 16rem;
  display: flex;
  gap: 1rem;
  flex-direction: column;
  overflow-y: auto;
  .bl-input-dropdown-option {
    cursor: pointer;
    position: relative;
    width: 100%;
    height: 2.5rem;
    padding: 1.8rem 0;
    border-bottom: 1px solid #eaecf0;
    display: flex;
    flex-shrink: 0;
    gap: 0.75rem;
    align-items: center;
    .bl-input-dropdown-option-name {
      pointer-events: none;
      position: relative;
      font-size: 1rem;
      font-weight: 400;
      display: flex;
      flex-direction: column;
      line-height: calc(5 / 3);
      letter-spacing: 0.5px;
    }
    .bl-input-dropdown-option-tag {
      pointer-events: none;
      position: relative;
      max-width: 100%;
      height: 1.5rem;
      padding: 0.125rem 0.5rem;
      // border-radius: 0.75rem;
      border-radius: 5px;
      box-sizing: border-box;
      font-size: 0.75rem;
      font-weight: 400;
      line-height: calc(5 / 3);
      letter-spacing: 0.5px;
    }
  }
  .bl-input-dropdown-date-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .bl-input-dropdown-date {
      position: relative;
      width: calc((100% - 1.25rem) / 2);
    }
    .bl-input-dropdown-date-divider {
      position: relative;
      width: 0.75rem;
      height: 0.125rem;
      background: var(--font-secondary-color);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/superAdmin/center/CenterInformation.vue